/**
 * @voltxampere/components — Dedicated AC source (Sprint 20)
 *
 * Sprint 20 closes D-20-3 — the "phantom" `ac-source` schema type
 * referenced by the SPICE codec, UI symbol, and inspector
 * formula-registry but never given a dedicated component
 * definition. The Şefim-approved policy (option C, additive) keeps
 * the existing `voltage-source.acMagnitude` / `acPhase` fields
 * intact (ngspice VAC syntax parity) AND introduces this dedicated
 * type for circuits authored from scratch in the simulator.
 *
 * Both source types share the same complex AC stamp (ω → 0+jω
 * Norton-equivalent) — Sprint 20 commit C1's `applyAC` for
 * `voltage-source` is a thin wrapper that reads the AC fields when
 * present, and `applyAC` for `ac-source` reads the dedicated
 * params. This keeps backward compat while letting new circuits
 * use a self-documenting source type.
 *
 * Param shape (per Sprint 20 prompt + plan):
 *
 *   amplitude: V (≥ 0, finite)         — peak amplitude
 *   phase:     degrees (-360 ≤ p ≤ 360) — initial phase
 *   dcOffset:  V (finite, optional)    — DC superposition (default 0)
 *   freq:      Hz (> 0, optional)      — single-frequency sinusoid
 *                                        for *transient* runs only;
 *                                        AC sweep ignores this.
 *
 * Type identifier `"ac-source"` matches SPECS §1.3 regex
 * `^[a-z][a-z0-9-]*$`. Pin layout mirrors `voltage-source`
 * (`pos` top, `neg` bottom) so the SPICE codec's element-codec
 * dispatch keeps working for both types via the same terminal
 * lookup.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const ACSourceParamsSchema = z
    .object({
    /** Peak amplitude in volts (≥ 0). */
    amplitude: z.number().nonnegative().finite(),
    /** Initial phase in degrees, default 0. Wraps to (-180, 180]. */
    phase: z.number().finite().default(0),
    /** Optional DC bias added to the AC waveform during transient
     *  analysis. Ignored by the AC sweep (which is small-signal
     *  around the DC operating point). Default 0 V. */
    dcOffset: z.number().finite().default(0),
    /** Optional single-tone frequency in Hz for transient. AC sweep
     *  uses `simulationSettings.ac.{start,stop,points,sweep}`
     *  instead. Defaults to 1 Hz when omitted (rarely meaningful —
     *  authors should set this explicitly in transient circuits). */
    freq: z.number().positive().finite().default(1),
})
    .strict();
export const acSourceDefinition = {
    type: "ac-source",
    category: "source",
    displayName: { tr: "AC Kaynak", en: "AC Source" },
    symbol: "VAC",
    pins: [
        { id: "pos", side: "top", offset: 0, label: "+" },
        { id: "neg", side: "bottom", offset: 0, label: "-" },
    ],
    parameterSchema: ACSourceParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=ac-source.js.map